import { z } from 'zod';
// Imported from the module directly rather than the utils barrel, so schemas
// and utils/index.ts cannot form an import cycle.
import { DimAxisEnum, DimLinkSchema } from './dim-link.schema';

// ═══════════════════════════════════════════════════
//  Shared upholstery design schemas
// ═══════════════════════════════════════════════════
//
// Neutral home for the design vocabulary every upholstered banquette component
// shares. This module exists because the dependency direction forbids the
// obvious alternative: banquette-back-cushion-template.schema.ts imports from
// banquette-base-template.schema.ts, so the base cannot re-export from the back
// cushion the way seat and arm do. Nothing here may import a component schema.

/**
 * The eight designs this app shipped with. **Not** a validation gate — the
 * `upholstery_designs` table is. This list exists for exactly three consumers:
 * UpholsteryDesignsSeedService, SEEDED_DESIGN_TRAITS, and the tests that assert
 * the two agree.
 *
 * Adding a design does NOT mean adding it here. It means inserting a row.
 */
export const SEEDED_UPHOLSTERY_DESIGN_KEYS = [
  'plain',
  'vertical_channel_tufted',
  'horizontal_channel_tufted',
  'diamond_button_tufted',
  'biscuit_button_tufted',
  'head_roll',
  'channel_seam',
  'diamond_seam',
] as const;

/**
 * A design key, validated by *shape* rather than by membership.
 *
 * This replaces the z.enum that used to live here. The catalog table is now the
 * source of truth for which designs exist, so an enum could only ever be a
 * stale copy of it — and filtering against that copy is exactly what stopped a
 * new row from reaching the dropdown.
 *
 * The regex is not decoration. The key is stored verbatim inside four TEXT
 * config columns, is a top-level object key in two JSONB maps, and appears in
 * the comma-separated labor_rates.design_type_filter. A key with a space or a
 * comma in it would silently fail to match itself in at least one of those.
 */
export const UpholsteryDesignKeySchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z][a-z0-9_]*$/, 'Key must be lowercase letters, digits and underscores, starting with a letter');

export const UpholsteryDesignSchema = z.object({
  type: UpholsteryDesignKeySchema.default('plain'),
  channel_width: z.number().min(0).nullable().default(null),
  channel_count_override: z.number().int().min(0).nullable().default(null),
  button_tuftings_per_foot: z.number().int().min(0).nullable().default(null),
  button_hardware_id: z.string().uuid().nullable().default(null),
  wire_hardware_id: z.string().uuid().nullable().default(null),
});

export const UpholsterySpringConfigSchema = z.object({
  springs_enabled: z.boolean().default(false),
  spring_hardware_id: z.string().uuid().nullable().default(null),
  spring_mode: z.enum(['auto', 'manual']).default('auto'),
  spring_spacing: z.number().min(0).default(4),
  spring_count: z.number().int().min(0).default(0),
  clip_hardware_id: z.string().uuid().nullable().default(null),
  connecting_wire_hardware_id: z.string().uuid().nullable().default(null),
  connecting_wire_count: z.number().int().min(0).default(1),
});

// ── Foam dimension links ──
//
// A foam dimension may track width, depth, or height. Unlike panels, foam may
// track width: on the three LF components that is the run, on the arm the plain
// width field. null (the default) means static.

// Kept as aliases: packages/shared/src/index.ts exports these names publicly and
// foam-dim-link.schema.test.ts imports them. The parity guard now lives with the
// enum in dim-link.schema.ts.
export const FoamDimAxisEnum = DimAxisEnum;
export const FoamDimLinkSchema = DimLinkSchema;

/** width is a nesting dimension; length is the run, with an Auto default. */
export const UpholsteryFoamLayerSchema = z.object({
  name: z.string(),
  width: z.number().min(0),
  /** When set, `width` is the base and tracks this component axis. */
  width_link: FoamDimLinkSchema,
  /** null = Auto: the layer runs the component's full width (lf_quantity * 12). */
  length: z.number().min(0).nullable().default(null),
  /** Only consulted when `length` is a number. */
  length_link: FoamDimLinkSchema,
  material_id: z.string().uuid().nullable(),
});

/** A design key. Widened from a closed union on 2026-08-21: the catalog table
 *  decides what exists, so no compile-time union can be complete. */
export type UpholsteryDesignType = z.infer<typeof UpholsteryDesignKeySchema>;
export type UpholsteryDesign = z.infer<typeof UpholsteryDesignSchema>;
export type UpholsterySpringConfig = z.infer<typeof UpholsterySpringConfigSchema>;
export type UpholsteryFoamLayer = z.infer<typeof UpholsteryFoamLayerSchema>;
export type FoamDimAxis = z.infer<typeof FoamDimAxisEnum>;
